import { useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Trans, useTranslation } from 'react-i18next';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import type { TFunction } from 'i18next';
import { useRequestPasswordReset } from '@/features/auth';
import { Button } from '@/shared/ui/Button';
import { AuthInput } from './ui/AuthInput';
import { AuthLayout } from './ui/AuthLayout';
import { authFormClassName, authMessageClassName, authServerErrorClassName } from './ui/authForm';

// RequestPasswordReset на новом стеке. Прямого 1:1 референса у фронтендера нет
// (у них на Login.tsx — placeholder <a href="#">Забыли пароль?</a>). Делаем
// по образцу Login: двухколоночный layout, react-hook-form + zod, useRequestPasswordReset
// mutation. Endpoint /user/requestResetPassword, бек отдаёт {status:1} либо
// {validation_error: {...}}.

const createRequestResetSchema = (t: TFunction<'auth'>) =>
  z.object({
    email: z.string().email(t('validation.emailInvalid')).toLowerCase().trim(),
  });

type RequestResetFormData = z.infer<ReturnType<typeof createRequestResetSchema>>;

export function RequestPasswordReset() {
  const { t } = useTranslation(['auth', 'common']);
  const requestResetSchema = useMemo(() => createRequestResetSchema(t), [t]);
  const navigate = useNavigate();
  const requestReset = useRequestPasswordReset();

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isSubmitSuccessful },
    setError: setFormError,
    clearErrors,
    getValues,
  } = useForm<RequestResetFormData>({
    resolver: zodResolver(requestResetSchema),
    mode: 'onSubmit',
    reValidateMode: 'onBlur',
    defaultValues: { email: '' },
  });

  const handleInputChange = (field: keyof RequestResetFormData) => {
    if (errors.root?.server) clearErrors('root.server');
    if (errors[field]) clearErrors(field);
  };

  const getShowError = (field: keyof RequestResetFormData) =>
    !!errors[field] || !!errors.root?.server;

  const onSubmit = async (data: RequestResetFormData) => {
    clearErrors('root.server');
    try {
      const outcome = await requestReset.mutateAsync({ email: data.email });
      switch (outcome.kind) {
        case 'ok':
          // Успешно — экран success рендерится через isSubmitSuccessful.
          return;
        case 'validation':
          if (outcome.errors.email?.[0]) {
            setFormError('email', {
              type: 'server',
              message: outcome.errors.email[0],
            });
          } else if (Object.keys(outcome.errors).length === 0) {
            setFormError('root.server', {
              type: 'manual',
              message: t('forgot.mailFailed'),
            });
          }
          break;
        case 'error':
          setFormError('root.server', {
            type: 'manual',
            message: t('forgot.mailFailedLater'),
          });
          break;
      }
    } catch {
      setFormError('root.server', {
        type: 'manual',
        message: t('common:networkError'),
      });
    }
  };

  const showSuccess = isSubmitSuccessful && !errors.root?.server && !errors.email;

  if (showSuccess) {
    return (
      <AuthLayout
        title={t('forgot.sentTitle')}
        questionText={t('rememberedPassword')}
        linkTo="/login"
        linkText={t('signIn')}
      >
        {/* Trans, а не t(): в строке жирным выделен только адрес, поэтому
            разметка приезжает из словаря вместе с текстом — иначе перевод
            пришлось бы резать на префикс и суффикс, и порядок слов в других
            языках зафиксировался бы русским. */}
        <p className={authMessageClassName()}>
          <Trans
            i18nKey="forgot.sentText"
            ns="auth"
            values={{ email: getValues('email') }}
            components={{ b: <b /> }}
          />
        </p>
        <div className="mt-8 w-full">
          <Button
            type="button"
            className="min-h-[48px] w-full !px-0"
            onClick={() => navigate('/login')}
          >
            {t('forgot.backToLogin')}
          </Button>
        </div>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout
      title={t('forgot.title')}
      questionText={t('rememberedPassword')}
      linkTo="/login"
      linkText={t('signIn')}
      hint={t('forgot.hint')}
    >
      <form
        onSubmit={handleSubmit(onSubmit)}
        noValidate
        className={authFormClassName()}
      >
        {errors.root?.server && (
          <div className={authServerErrorClassName()}>
            {errors.root.server.message}
          </div>
        )}

        <div className="relative w-full">
          <AuthInput
            type="email"
            autoComplete="email"
            label={t('fields.email')}
            example={t('examples.email')}
            error={errors.email?.message}
            showError={getShowError('email')}
            {...register('email', { onChange: () => handleInputChange('email') })}
          />
        </div>

        <div className="mt-[6px]">
          <Button
            type="submit"
            disabled={isSubmitting}
            isLoading={isSubmitting}
            className="min-h-[48px] w-full !px-0"
          >
            {t('forgot.submit')}
          </Button>
        </div>
      </form>
    </AuthLayout>
  );
}
